import { rgba } from 'polished';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Input } from '../form/controls/Input';
import { CheckmarkIcon, CloseIcon, MagicWandIcon, SearchIcon } from '../icons';
import { Button } from './button/Button';
import { Popover } from './Popover';
import { Select } from './Select';
import { Separator } from './Separator';
import { Label } from './Text';

const CustomContainer = styled(Flex)`
	font-size: 14px;
	line-height: 22px;
	padding: 6px;
	background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	border-radius: 5px;
	color: ${(props) => props.theme.colors.primary};
	cursor: pointer;

	svg {
		display: block;
		width: 24px;
		height: 24px;
		margin: -1px;

		path {
			fill: ${(props) => props.theme.colors.primary};
		}
	}

	&:hover {
		background: ${(props) => rgba(props.theme.colors.primary, 0.25)};

		&:active {
			background: ${(props) => rgba(props.theme.colors.primary, 0.5)};
		}
	}
`;

export function Combo(
	props: Omit<ComponentProps<typeof Select.Menu>, 'onChange'> & {
		autoFocus?: boolean;
		custom?: boolean;
		fetch?: () => void;
		explanation?: string;
		placeholder?: string;
		preSelectedValue?: string;
		choice?: { custom: string } | { value: string };
		onChange: (choice?: { custom: string } | { value: string }) => void;
		clearInputOnSelection?: boolean;
	},
) {
	const { t } = useTranslation();

	const [value, setValue] = useState(props.preSelectedValue ?? '');
	const [focus, setFocus] = useState(false);
	const [autoFocus, setAutoFocus] = useState(props.autoFocus);
	const focusTimerRef = useRef<ReturnType<typeof setTimeout>>();

	const closeMenu = () => {
		clearTimeout(focusTimerRef.current);
		setFocus(false);
	};

	useEffect(() => () => clearTimeout(focusTimerRef.current), []);

	const { fetch } = props;

	useEffect(() => {
		if (focus) {
			fetch?.();
		}
	}, [focus]);

	return (
		<Popover.Target
			visible={focus}
			contents={
				<Box>
					<Select.Menu
						{...props}
						query={value}
						search={false}
						onChange={(value, option) => {
							if (value) {
								props.onChange({ value });
							} else {
								props.onChange(undefined);
							}
							if (!props.clearInputOnSelection) {
								setValue(option?.title ?? '');
							}
							closeMenu();
						}}
					/>
					{props.custom !== false && (
						<>
							<Box px={3}>
								<Separator.Inlay>
									<Label>{t('ui.combo.or')}</Label>
								</Separator.Inlay>
							</Box>
							<Box p={1}>
								<Button
									type="transparent"
									small
									title={t('ui.combo.createButton')}
									onClick={() => {
										closeMenu();
										props.onChange({ custom: value });
									}}
								/>
							</Box>
						</>
					)}
				</Box>
			}
			onDismiss={closeMenu}
		>
			{props.choice && 'custom' in props.choice ? (
				<>
					<CustomContainer
						onClick={() => {
							props.onChange(undefined);
							setFocus(true);
							setAutoFocus(true);
						}}
					>
						<MagicWandIcon />
						<Box flexGrow={1} flexShrink={1} mx={2}>
							{value}
						</Box>
						<CloseIcon />
					</CustomContainer>
					{props.explanation && <Label mt={2}>{props.explanation}</Label>}
				</>
			) : (
				<Input
					autoFocus={autoFocus}
					icon={props.choice ? <CheckmarkIcon /> : <SearchIcon />}
					small
					value={value}
					placeholder={
						props.placeholder ??
						(props.custom === false ? t('ui.select.placeholder') : t('ui.combo.placeholder'))
					}
					onFocus={() => {
						clearTimeout(focusTimerRef.current);
						focusTimerRef.current = setTimeout(() => {
							setFocus(true);
						}, 400);
					}}
					onChange={(value) => {
						clearTimeout(focusTimerRef.current);
						props.onChange(undefined);
						setValue(value);
						setFocus(true);
					}}
				/>
			)}
		</Popover.Target>
	);
}
